import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  HeartPulse,
  Brain,
  Users,
  Sprout,
  Stethoscope,
  Apple,
  HandHeart,
  Leaf,
  MessageCircleHeart,
  HeartHandshake,
  Handshake,
  CheckCircle2,
} from "lucide-react";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Reveal, StaggerGroup, StaggerItem } from "../components/Reveal";
import { BlobShape, LeafBranch, FloralAccent } from "../components/Organic";
import { FOCUS_AREAS, TOOLKIT, PARTNERS } from "../lib/content";

const ICONS = {
  HeartPulse, Brain, Users, Sprout,
  Stethoscope, Apple, HandHeart, Leaf,
  MessageCircleHeart, HeartHandshake,
};

export default function Services() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [location]);

  return (
    <div data-testid="page-services">
      <Header />
      <SpecialtySpotlights />
      <FocusGrid />
      <Toolkit />
      <HolisticNetwork />
      <CtaSection />
    </div>
  );
}

function Header() {
  return (
    <section className="relative pt-44 md:pt-52 pb-16 md:pb-20 px-6 md:px-10 overflow-hidden">
      <BlobShape
        className="absolute -top-20 right-0 w-[520px] h-[520px]"
        color="#E4BE9E"
        opacity={0.22}
      />
      <FloralAccent
        className="absolute top-24 left-10 w-32 h-32 hidden md:block"
        color="#2A7B76"
        opacity={0.18}
      />
      <div className="relative max-w-5xl mx-auto text-center">
        <Reveal>
          <span className="overline">Services & Specializations</span>
        </Reveal>
        <Reveal delay={1}>
          <h1 className="h-display mt-5 text-5xl md:text-6xl lg:text-7xl">
            Building resilience,{" "}
            <span className="h-italic-accent">connection,</span> and growth.
          </h1>
        </Reveal>
        <Reveal delay={2}>
          <p className="mt-7 text-lg md:text-xl text-[var(--at-navy-soft)] max-w-3xl mx-auto leading-relaxed">
            Compassionate, person-centered therapy supporting children, teens,
            adults, and caregivers. I combine clinical expertise with gentle
            empathy — meeting you where you are and building tools you can
            actually use.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function SpecialtySpotlights() {
  const specialties = [
    {
      id: "anxiety",
      overline: "Specialty",
      title: "Anxiety Disorders",
      lead:
        "Anxiety can affect nearly every aspect of daily life — from relationships and work performance to sleep and overall well-being.",
      body: [
        "Whether you're experiencing persistent worry, panic attacks, social anxiety, health-related fears, or overwhelming stress, therapy can help you better understand your anxiety and develop effective coping strategies.",
        "Together, we'll identify patterns that contribute to anxiety, build emotional resilience, and create practical tools for managing symptoms. The goal is not simply to reduce anxiety, but to help you regain confidence, increase your sense of control, and engage more fully in the life you want to live.",
      ],
      bullets: [
        "Persistent worry & generalized anxiety",
        "Panic attacks",
        "Social anxiety",
        "Health-related fears",
        "Overwhelming daily stress",
      ],
      image:
        "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=1200&q=80",
      imageAlt:
        "Person taking a calming breath in soft natural light",
    },
    {
      id: "depression",
      overline: "Specialty",
      title: "Depression & Emotional Wellness",
      lead:
        "Depression can make even simple tasks feel heavy and isolating. Taking the first step toward support is an act of courage.",
      body: [
        "I offer a compassionate environment where you can feel heard, understood, and accepted. Through therapy, we'll explore the factors contributing to low mood, loss of motivation, or emotional exhaustion, and identify strategies to foster healing and growth.",
        "My goal is to help you reconnect with your values, rediscover hope, and build the tools needed to navigate life's challenges with greater clarity and confidence.",
      ],
      bullets: [
        "Low mood & emotional exhaustion",
        "Loss of motivation",
        "Grief & loss",
        "Self-worth & identity",
        "Rebuilding hope and clarity",
      ],
      image:
        "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?auto=format&fit=crop&w=1200&q=80",
      imageAlt:
        "Warm, candid moment of self-reflection by a sunlit window",
    },
  ];

  return (
    <section
      data-testid="services-specialties"
      className="py-20 md:py-28 px-6 md:px-10"
    >
      <div className="max-w-7xl mx-auto space-y-20 md:space-y-28">
        {specialties.map((s, idx) => (
          <div
            key={s.id}
            id={s.id}
            className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center scroll-mt-32`}
          >
            <div
              className={`lg:col-span-6 ${
                idx % 2 === 1 ? "lg:order-2" : ""
              }`}
            >
              <Reveal>
                <div className="relative">
                  <div className={`absolute ${idx % 2 === 0 ? "-top-6 -left-6" : "-top-6 -right-6"} w-36 h-36 bg-[var(--at-teal)]/15 blob`} />
                  <img
                    src={s.image}
                    alt={s.imageAlt}
                    className={`relative w-full h-[440px] md:h-[520px] object-cover shadow-[0_30px_60px_-30px_rgba(26,46,68,0.35)] ${
                      idx % 2 === 0 ? "mask-organic-1" : "mask-organic-2"
                    }`}
                  />
                </div>
              </Reveal>
            </div>
            <div
              className={`lg:col-span-6 ${
                idx % 2 === 1 ? "lg:order-1" : ""
              }`}
            >
              <Reveal>
                <span className="overline">{s.overline}</span>
                <h2
                  data-testid={`specialty-title-${s.id}`}
                  className="h-display mt-3 text-4xl md:text-5xl"
                >
                  {s.title}
                </h2>
                <p className="mt-5 text-lg text-[var(--at-navy)] font-medium leading-relaxed">
                  {s.lead}
                </p>
                <div className="mt-5 space-y-4 text-[var(--at-navy-soft)] leading-relaxed">
                  {s.body.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
                <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
                  {s.bullets.map((b) => (
                    <li
                      key={b}
                      className="flex items-start gap-2 text-[var(--at-navy)] text-sm"
                    >
                      <CheckCircle2
                        size={16}
                        className="mt-0.5 text-[var(--at-teal)] shrink-0"
                      />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function FocusGrid() {
  return (
    <section
      data-testid="services-focus"
      className="py-20 md:py-28 px-6 md:px-10 bg-[var(--at-bg-2)]"
    >
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <span className="overline">Additional Areas of Focus</span>
          <h2 className="h-display mt-4 text-3xl md:text-4xl max-w-2xl">
            Care across the full{" "}
            <span className="h-italic-accent">spectrum of life.</span>
          </h2>
        </Reveal>

        <StaggerGroup className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          {FOCUS_AREAS.map((f) => {
            const Icon = ICONS[f.icon] || HeartPulse;
            return (
              <StaggerItem key={f.id}>
                <div
                  data-testid={`service-focus-${f.id}`}
                  className="bento-card h-full rounded-[2rem] bg-[var(--at-bg)] border border-[var(--at-border)] p-8 md:p-10 flex gap-6"
                >
                  <div className="shrink-0 w-14 h-14 rounded-2xl bg-[var(--at-teal-tint)] flex items-center justify-center text-[var(--at-teal)]">
                    <Icon size={26} />
                  </div>
                  <div>
                    <h3 className="font-serif text-2xl text-[var(--at-navy)] leading-snug">
                      {f.title}
                    </h3>
                    <p className="mt-3 text-[var(--at-navy-soft)] leading-relaxed">
                      {f.body}
                    </p>
                  </div>
                </div>
              </StaggerItem>
            );
          })}
        </StaggerGroup>
      </div>
    </section>
  );
}

function Toolkit() {
  return (
    <section
      data-testid="services-toolkit"
      className="relative py-24 md:py-32 px-6 md:px-10 overflow-hidden noise"
    >
      <LeafBranch
        className="absolute top-10 right-8 w-32 h-32 hidden md:block"
        color="#8DAA91"
        opacity={0.35}
      />
      <div className="relative max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-14">
          <Reveal>
            <div className="lg:col-span-7">
              <span className="overline">My Therapeutic Toolkit</span>
              <h2 className="h-display mt-4 text-4xl md:text-5xl">
                Methods that support{" "}
                <span className="h-italic-accent">lasting change.</span>
              </h2>
            </div>
          </Reveal>
          <Reveal delay={1}>
            <p className="lg:col-span-5 text-lg text-[var(--at-navy-soft)] leading-relaxed">
              No one therapy fits everyone. I integrate proven, evidence-based
              modalities — tailored to your goals and experience.
            </p>
          </Reveal>
        </div>

        <StaggerGroup className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {TOOLKIT.map((t) => (
            <StaggerItem key={t.title}>
              <div
                data-testid={`toolkit-${t.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="bento-card h-full rounded-[1.75rem] bg-[var(--at-bg-2)] border border-[var(--at-border)] p-8"
              >
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold tracking-wider bg-[var(--at-navy)] text-[var(--at-bg)]">
                  {t.label}
                </span>
                <h3 className="mt-5 font-serif text-xl text-[var(--at-navy)] leading-snug">
                  {t.title}
                </h3>
                <p className="mt-3 text-[var(--at-navy-soft)] leading-relaxed">
                  {t.body}
                </p>
              </div>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </div>
    </section>
  );
}

function HolisticNetwork() {
  return (
    <section
      id="holistic-care"
      data-testid="services-holistic"
      className="py-24 md:py-32 px-6 md:px-10 scroll-mt-32"
    >
      <div className="max-w-7xl mx-auto relative rounded-[3rem] bg-[var(--at-navy)] text-[var(--at-bg)] px-8 md:px-16 py-20 md:py-28 overflow-hidden">
        <BlobShape
          className="absolute -top-32 -right-32 w-[560px] h-[560px]"
          color="#2A7B76"
          opacity={0.3}
        />
        <BlobShape
          className="absolute -bottom-40 -left-32 w-[480px] h-[480px]"
          color="#8DAA91"
          opacity={0.22}
        />
        <LeafBranch
          className="absolute top-10 right-14 w-40 h-40 hidden md:block"
          color="#8DAA91"
          opacity={0.45}
        />

        <div className="relative max-w-3xl">
          <Reveal>
            <span className="inline-flex items-center gap-2 overline text-[var(--at-olive)] bg-white/5 px-4 py-1.5 rounded-full">
              <Handshake size={14} /> Holistic Care
            </span>
            <h2 className="h-display mt-6 text-4xl md:text-5xl lg:text-6xl text-[var(--at-bg)]">
              My collaborative{" "}
              <span className="italic text-[var(--at-olive)]">network</span>{" "}
              of partners.
            </h2>
            <p className="mt-6 text-lg text-[var(--at-bg)]/85 leading-relaxed">
              At All Thrive Therapy and Consulting, PLLC I believe true
              wellness requires looking at the whole picture. I partner with
              leading healthcare and wellness providers — so if your journey
              calls for medical coordination, nutritional support, occupational
              therapy, or holistic wellness, your care stays integrated and
              unified.
            </p>
          </Reveal>
        </div>

        <StaggerGroup className="relative mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {PARTNERS.map((p) => {
            const Icon = ICONS[p.icon] || Leaf;
            return (
              <StaggerItem key={p.name}>
                <div
                  data-testid={`partner-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="rounded-2xl bg-white/5 border border-white/10 p-5 backdrop-blur-sm h-full"
                >
                  <div className="w-11 h-11 rounded-xl bg-[var(--at-olive)]/25 flex items-center justify-center text-[var(--at-olive)]">
                    <Icon size={20} />
                  </div>
                  <p className="mt-4 text-sm text-[var(--at-bg)]/90 leading-snug font-medium">
                    {p.name}
                  </p>
                </div>
              </StaggerItem>
            );
          })}
        </StaggerGroup>

        <Reveal delay={2}>
          <div className="relative mt-12 flex flex-col md:flex-row gap-4 items-start md:items-center">
            <Link
              to="/contact"
              data-testid="holistic-cta-button"
              className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-semibold bg-[var(--at-olive)] text-[var(--at-navy)] hover:bg-[var(--at-bg)] transition-colors"
            >
              Ask About Coordinated Care <ArrowUpRight size={18} />
            </Link>
            <p className="text-sm text-[var(--at-bg)]/65 max-w-md">
              Already working with a provider? I'll happily collaborate with
              your existing team to keep care unified.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function CtaSection() {
  return (
    <section className="px-6 md:px-10 pb-24" data-testid="services-cta">
      <div className="max-w-5xl mx-auto rounded-[2.5rem] bg-[var(--at-bg-2)] border border-[var(--at-border)] p-10 md:p-16 text-center relative overflow-hidden">
        <FloralAccent
          className="absolute -top-12 left-1/2 -translate-x-1/2 w-32 h-32"
          color="#2A7B76"
          opacity={0.18}
        />
        <Reveal>
          <h2 className="h-display text-3xl md:text-5xl">
            Not sure where to{" "}
            <span className="h-italic-accent">start?</span>
          </h2>
          <p className="mt-5 text-lg text-[var(--at-navy-soft)] max-w-2xl mx-auto">
            Book a free consultation — I'll listen first, then help you map
            out the right path forward.
          </p>
          <Link
            to="/contact"
            data-testid="services-bottom-cta"
            className="btn-primary mt-9"
          >
            Schedule a Free Consultation <ArrowUpRight size={18} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}
